import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Music, Users, DollarSign, Award, Check } from 'react-feather';
import { getPublicEvents } from '../services/api';
import {Event} from "../types/Event";

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await getPublicEvents();
        setEvents(response.data.slice(0, 6)); // Show only first 6 events
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero custom-background-color text-white py-5">
        <Container className="position-relative">
          <Row className="align-items-center min-vh-75">
            <Col lg={6} className="order-1 order-lg-2">
              <div className="floating-device">
                <img
                    src="/slideshow.png?v1"
                    alt="MyWeeshy App Preview"
                    className="img-fluid"
                />
              </div>
            </Col>
            <Col lg={6} className="order-2 order-lg-1">
              <h1 className="display-3 fw-bold mb-4 animate__animated animate__fadeInUp">
                Make Every Event <span className="custom-purple-icon">Unforgettable</span>
              </h1>
              <p className="lead mb-4 animate__animated animate__fadeInUp animate__delay-1s">
                Transform your events with real-time song requests, seamless playlist management, and instant audience engagement.
                <i className="fas fa-music custom-purple-icon ms-2"></i>
              </p>
              <div className="animate__animated animate__fadeInUp animate__delay-2s">
                <Link
                    to="/signup"
                    className="btn custom-purple-btn btn-lg me-3 mb-2 shadow text-light hover-text-warning"
                >
                  <i className="fas fa-rocket me-2 text-light"></i>Get Started Free
                </Link>
                <a href="#features" className="btn btn-outline-light btn-lg mb-2">
                  <i className="fas fa-play me-2"></i>See How It Works
                </a>
              </div>
              <div className="mt-4 animate__animated animate__fadeInUp animate__delay-3s">
                <div className="d-flex align-items-center">
                  <div className="me-4">
                    <span className="h2 fw-bold mb-0">1000+</span>
                    <p className="small mb-0">Active DJs</p>
                  </div>
                  <div className="me-4">
                    <span className="h2 fw-bold mb-0">50K+</span>
                    <p className="small mb-0">Song Requests</p>
                  </div>
                  <div>
            <span className="h2 fw-bold mb-0">
              4.9 <i className="fas fa-star text-warning me-2"></i>
            </span>
                    <p className="small mb-0">User Rating</p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>

      </section>

      {/* Features Section */}
      <section className="features py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">Powerful Features for Modern DJs</h2>
            <p className="lead text-muted">Everything you need to create unforgettable events</p>
          </div>
          <Row className="g-4">
            <Col md={4}>
              <Card className="feature-card text-center p-5 border-0">
                <div className="feature-icon mb-4">
                  <i className="fas fa-qrcode fa-3x custom-purple-icon"></i>
                </div>
                <h4>Instant QR Code Access</h4>
                <p className="text-muted mb-0">
                  Generate unique QR codes for each event. Let guests scan and submit requests in seconds.
                </p>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="feature-card text-center p-5 border-0">
                <div className="feature-icon mb-4">
                  <i className="fas fa-stream fa-3x custom-purple-icon"></i>
                </div>
                <h4>Real-time Queue</h4>
                <p className="text-muted mb-0">
                  Manage song requests in real-time. Keep your audience engaged with live updates.
                </p>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="feature-card text-center p-5 border-0">
                <div className="feature-icon mb-4">
                  <i className="fas fa-hand-holding-heart fa-3x custom-purple-icon"></i>
                </div>
                <h4>Tip Integration</h4>
                <p className="text-muted mb-0">
                  Accept tips directly through the platform. Make it easy for guests to show appreciation.
                </p>
              </Card>
            </Col>
          </Row>


        </Container>
      </section>

      {/* Active Events Section */}
      <section className="events py-5 bg-light">
        <Container fluid className="py-5" style={{ backgroundColor: '#1C2333' }}>
          <Container>
            <Row className="text-center mb-5">
              <Col>
                <h2 className="display-5 fw-bold mb-3 text-white">What DJs Say About Us</h2>
                <p className="lead text-light">Join thousands of satisfied DJs worldwide</p>
              </Col>
            </Row>

            <Row className="g-4">
              {[
                {
                  quote:
                      '"MyWeeshy has transformed how I handle requests. The interface is intuitive, and my clients love it!"',
                  name: 'John Smith',
                  title: 'Wedding DJ, New York',
                },
                {
                  quote:
                      '"The QR code feature is genius! No more paper requests or shouting over the music. Simply brilliant!"',
                  name: 'Sarah Johnson',
                  title: 'Club DJ, Los Angeles',
                },
                {
                  quote:
                      '"The premium features are worth every penny. My tips have increased since I started using the platform!"',
                  name: 'Mike Davis',
                  title: 'Bar DJ, Chicago',
                },
              ].map((testimonial, idx) => (
                  <Col md={4} key={idx}>
                    <Card className="feature-card h-100 border-0 shadow-sm hover-lift text-light p-5 custom-purple-btn">
                      <div className="mb-4">
                        {[...Array(5)].map((_, i) => (
                            <i key={i} className="fas fa-star text-warning me-1"></i>
                        ))}
                      </div>
                      <p className="mb-4">{testimonial.quote}</p>
                      <div className="d-flex align-items-center">
                        <div className="avatar me-3">
                          <i className="fas fa-user-circle fa-2x text-light"></i>
                        </div>
                        <div>
                          <h6 className="mb-1 text-white">{testimonial.name}</h6>
                          <small className="text-white">{testimonial.title}</small>
                        </div>
                      </div>
                    </Card>
                  </Col>
              ))}
            </Row>
          </Container>

        </Container>
      </section>


      {/* Pricing Section */}
      <section className="pricing py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="fw-bold">{t('home.pricing.title')}</h2>
              <p className="lead text-muted">{t('home.pricing.subtitle')}</p>
            </Col>
          </Row>

          <Row className="justify-content-center g-4">
            <Col md={6} lg={4}>
              <Card className="feature-card h-100 shadow-sm">
                <Card.Body className="p-4 text-center">
                  <Card.Title className="fw-bold">{t('home.pricing.free.title')}</Card.Title>
                  <div className="price-tag my-4">
                    <span className="h1 fw-bold">€0</span>
                    <span className="text-muted">/{t('home.pricing.month')}</span>
                  </div>
                  <ul className="list-unstyled text-start">
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.free.feature1')}
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.free.feature2')}
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.free.feature3')}
                    </li>
                  </ul>
                  <div className="d-grid mt-4">
                    <Button
                        as={Link as unknown as any}
                        to="/signup"
                        variant="primary"
                        className="custom-purple-btn"
                    >
                      {t('home.pricing.free.button')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="feature-card h-100 shadow">
                <div className="custom-purple-btn text-white text-center py-2">
                  <span className="fw-bold">{t('home.pricing.popular')}</span>
                </div>
                <Card.Body className="p-4 text-center">
                  <Card.Title className="fw-bold">{t('home.pricing.premium.title')}</Card.Title>
                  <div className="price-tag my-4">
                    <span className="h1 fw-bold">€5.99</span>
                    <span className="text-muted">/{t('home.pricing.month')}</span>
                  </div>
                  <ul className="list-unstyled text-start">
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.premium.feature1')}
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.premium.feature2')}
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.premium.feature3')}
                    </li>
                    {/*<li className="mb-3 d-flex align-items-center">*/}
                    {/*  <Check className="text-success me-2" size={20} />*/}
                    {/*  {t('home.pricing.premium.feature4')}*/}
                    {/*</li>*/}
                  </ul>
                  <div className="d-grid mt-4">
                    <Button
                        as={Link as unknown as any}
                        to="/signup"
                        variant="primary"
                        className="custom-purple-btn"
                    >
                      {t('home.pricing.free.button')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} lg={4}>
              <Card className="feature-card h-100 shadow">

                <Card.Body className="p-4 text-center">
                  <Card.Title className="fw-bold">Expert</Card.Title>
                  <div className="price-tag my-4">
                    <span className="h1 fw-bold">€9.99</span>
                    <span className="text-muted">/{t('home.pricing.month')}</span>
                  </div>
                  <ul className="list-unstyled text-start">
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.premium.feature1')}
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.premium.feature2')}
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.premium.feature3')}
                    </li>
                    <li className="mb-3 d-flex align-items-center">
                      <Check className="text-success me-2" size={20} />
                      {t('home.pricing.premium.feature4')}
                    </li>
                  </ul>
                  <div className="d-grid mt-4">
                    <Button
                      as={Link as unknown as any}
                      to="/signup"
                      variant="primary"
                      className="custom-purple-btn"
                    >
                      {t('home.pricing.free.button')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta text-white py-5 custon-home-cta">
        <Container>
          <Row className="justify-content-center text-center py-4">
            <Col md={8}>
              <h2 className="fw-bold mb-4">{t('home.cta.title')}</h2>
              <p className="lead mb-4">{t('home.cta.subtitle')}</p>
              <Button
                as={Link as unknown as any}
                to="/signup"
                variant="light"
                size="lg"
                className="fw-bold"
              >
                {t('home.cta.button')}
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default HomePage;
